import { useRouteContext } from '@/app/layouts/TenantLayout'
import { AccessControlled } from '@/components/access-control'
import { Input } from '@/components/ui/input'
import { ListSkeleton } from '@/components/ui/skeletons'
import { cn } from '@/lib/utils'
import { isPlatformUser } from '@/stores/auth'
import { useAuthStore, useCurrentTenant } from '@/stores/authStore'
import { useCanCreateProject } from '@/utils/permissions'
import { Check, Pin, Plus, Search } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { useDefaultProject, useProjectSwitch, useProjects, useSetDefaultProject } from './useSwitcherData'

interface ProjectSwitcherProps {
	onProjectSelect?: (tenantId: string) => void
	onSuccess?: () => void
	onCreateProjectClick: () => void
}

export function ProjectSwitcher({ onProjectSelect, onSuccess, onCreateProjectClick }: ProjectSwitcherProps) {
	const [searchTerm, setSearchTerm] = useState('')
	const canCreateProject = useCanCreateProject()
	const user = useAuthStore(s => s.user)
	// Pins are personal (stored in user settings), so any platform user can pin — not just owners.
	const canPinDefaultProject = isPlatformUser(user)

	const { orgId, tenantId } = useRouteContext()
	const { filteredTenants, tenants, isFiltered, isLoading } = useProjects({ searchTerm, orgIdOverride: orgId })
	const currentTenant = useCurrentTenant()
	const resolvedActiveTenantId = useMemo(() => {
		if (tenantId) {
			return tenantId
		}
		if (currentTenant && tenants.some(t => t.id === currentTenant)) {
			return currentTenant
		}
		return undefined
	}, [tenantId, currentTenant, tenants])
	const { handleProjectSwitch: doProjectSwitch } = useProjectSwitch({ onProjectSelect })
	const defaultProjectId = useDefaultProject(orgId ?? null)
	const { setDefault, isPinning } = useSetDefaultProject()

	const handleProjectSwitch = useCallback(
		async (tenantId: string) => {
			await doProjectSwitch(tenantId, {
				onSuccess: () => {
					setSearchTerm('')
					onSuccess?.()
				},
			})
		},
		[doProjectSwitch, onSuccess],
	)

	const handlePinClick = useCallback(
		(e: React.MouseEvent, projectId: string) => {
			e.stopPropagation()
			if (!orgId || isPinning) {
				return
			}
			const newDefault = projectId === defaultProjectId ? null : projectId
			setDefault(orgId, newDefault)
		},
		[orgId, defaultProjectId, setDefault, isPinning],
	)

	const handleCreateClick = useCallback(() => {
		onCreateProjectClick()
	}, [onCreateProjectClick])

	return (
		<div className='w-full'>
			<div className='relative mb-2'>
				<Search className='absolute left-3 top-1/2 -translate-y-1/2 icon-alone text-muted-foreground' />
				<Input
					placeholder='Find project...'
					value={searchTerm}
					onChange={e => setSearchTerm(e.target.value)}
					className='h-9 pl-9 bg-transparent border-0 border-b border-border rounded-none text-body placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:border-border'
				/>
			</div>

			<div className='space-y-0.5 max-h-48 overflow-y-auto py-1'>
				{isLoading ? (
					<ListSkeleton count={3} itemHeight='h-10' />
				) : filteredTenants.length > 0 ? (
					filteredTenants.map(tenant => {
						const isActive = tenant.id === resolvedActiveTenantId
						const isPinned = tenant.id === defaultProjectId
						return (
							<button
								type='button'
								key={tenant.id}
								className={cn(
									'group/item w-full flex items-center justify-between p-(--switcher-item-padding) text-body text-left rounded-md transition-colors cursor-pointer',
									isActive ? 'bg-accent text-accent-foreground' : 'hover:bg-accent hover:text-accent-foreground',
								)}
								onClick={() => !isActive && handleProjectSwitch(tenant.id)}>
								<span className='text-foreground truncate'>{tenant.name}</span>
								<div className='flex items-center gap-1 ml-2 shrink-0'>
									{canPinDefaultProject && (
										<button
											type='button'
											className={cn(
												'p-0.5 rounded-sm transition-opacity cursor-pointer',
												isPinned
													? 'opacity-100 text-primary'
													: 'opacity-0 group-hover/item:opacity-100 text-muted-foreground hover:text-foreground',
											)}
											onClick={e => handlePinClick(e, tenant.id)}
											disabled={isPinning}
											title={isPinned ? 'Unpin default project' : 'Set as default project'}>
											<Pin
												className={cn(
													'size-(--icon-standalone) rotate-45 transform shrink-0',
													isPinned && 'text-primary',
												)}
												fill={isPinned ? 'currentColor' : 'none'}
												strokeWidth={2}
											/>
										</button>
									)}
									{isActive && <Check className='icon-alone text-foreground' strokeWidth={2} />}
								</div>
							</button>
						)
					})
				) : (
					<div className='text-center text-body text-muted-foreground py-4'>
						{isFiltered ? 'No projects found' : 'No projects available'}
					</div>
				)}
			</div>

			{/* AccessControlled: quota/plan limits; button may stay visible with a popover when at cap. */}
			{canCreateProject && (
				<div className='border-t border-border mt-1 pt-1 space-y-0.5'>
					<AccessControlled resource='projects' action='create'>
						<button
							type='button'
							className='w-full flex items-center gap-2 p-(--switcher-item-padding) text-body text-left rounded-md transition-colors cursor-pointer hover:bg-accent hover:text-accent-foreground text-foreground'
							onClick={handleCreateClick}>
							<Plus className='icon-alone' strokeWidth={2} />
							New project
						</button>
					</AccessControlled>
				</div>
			)}
		</div>
	)
}
